import * as Sentry from "@sentry/react";
import { Database, Layers, Send, Settings } from "lucide-react";
import { AssetsIcon } from "@/pages/Assets";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useEditorStore } from "@/store/editorStore";

// Custom variables icon - hexagon with centered circle
const VariablesIcon = ({ size = 16, className = "" }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <title>Variables</title>
    {/* Hexagon outline */}
    <polygon
      points="12,2 20,6 20,18 12,22 4,18 4,6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    {/* Centered circle */}
    <circle cx="12" cy="12" r="4" fill="currentColor" />
  </svg>
);

// Custom target icon - concentric circles
const TargetIcon = ({ size = 16, className = "" }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <title>Target</title>
    {/* Outer circle */}
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
    {/* Inner circle */}
    <circle cx="12" cy="12" r="4" fill="currentColor" />
  </svg>
);

export const VerticalNav = () => {
  const { project } = useEditorStore();
  const location = useLocation();
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  if (!project) return null;

  const handleSubmitReport = async () => {
    if (!reportText.trim()) return;

    setIsSubmittingReport(true);
    Sentry.withScope((scope) => {
      scope.setTag("user_report", "true");
      scope.setContext("user_report", {
        description: reportText.trim(),
        url: window.location.href,
      });
      scope.setLevel("error");
      Sentry.captureMessage("User report", "error");
    });
    setIsSubmittingReport(false);
    setIsReportOpen(false);
    setReportText("");
  };

  const mainNavItems = [
    {
      icon: TargetIcon,
      label: "Hotspot Editor",
      href: `/${project.id}/editor`,
      isActive: location.pathname.includes("/editor") || location.pathname === `/${project.id}`,
    },
    {
      icon: Layers,
      label: "Skin",
      href: `/${project.id}/skin`,
      isActive: location.pathname.includes("/skin"),
    },
    {
      icon: VariablesIcon,
      label: "Project Variables",
      href: `/${project.id}/variables`,
      isActive: location.pathname.includes("/variables"),
    },
    {
      icon: Database,
      label: "CMS",
      href: `/${project.id}/cms`,
      isActive: location.pathname.includes("/cms"),
    },
    {
      icon: AssetsIcon,
      label: "Assets",
      href: `/${project.id}/assets`,
      isActive: location.pathname.includes("/assets"),
    },
    {
      icon: Settings,
      label: "Project Settings",
      href: `/${project.id}/settings`,
      isActive: location.pathname.includes("/settings"),
    },
  ];


  return (
    <TooltipProvider delayDuration={0}>
      <div
        className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-12 flex flex-col items-center py-4 z-30 bg-gray-800 border-r border-gray-700"
      >
        {/* Main navigation items */}
        <div className="flex flex-col items-center">
          {mainNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <Tooltip key={item.label}>
                <TooltipTrigger asChild>
                  <Link
                    to={item.href}
                    className={`w-10 h-10 flex items-center justify-center rounded-md mb-2 transition-colors ${
                      item.isActive
                        ? "bg-gray-600 text-white"
                        : "text-gray-400 hover:text-white hover:bg-gray-700"
                    }`}
                  >
                    <Icon size={20} />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-gray-800 text-white border-gray-600">
                  {item.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* Bottom actions */}
        <div className="mt-auto">
          <Dialog open={isReportOpen} onOpenChange={setIsReportOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="w-10 h-10 flex items-center justify-center rounded-md transition-colors text-gray-400 hover:text-white hover:bg-gray-700 mb-2"
                  onClick={() => setIsReportOpen(true)}
                  aria-label="Report issue"
                >
                  <Send size={20} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-gray-800 text-white border-gray-600">
                Report Issue
              </TooltipContent>
            </Tooltip>
            <DialogContent className="max-w-md border border-gray-700 bg-gray-800 text-gray-100">
              <DialogHeader>
                <DialogTitle>Report an issue</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Tell us what happened and what you were trying to do.
                </DialogDescription>
              </DialogHeader>
              <Textarea
                value={reportText}
                onChange={(event) => setReportText(event.target.value)}
                placeholder="Example: The panorama failed to load after I clicked Preview."
                className="min-h-[120px] bg-gray-700 border-gray-600 text-white"
              />
              <DialogFooter className="gap-2 sm:gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-gray-300 hover:text-white hover:bg-gray-800"
                  onClick={() => setIsReportOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmitReport}
                  disabled={!reportText.trim() || isSubmittingReport}
                  className="bg-blue-600 hover:bg-blue-500"
                >
                  Send report
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </div>
      </div>
    </TooltipProvider>
  );
};
